/**
 * ThemeManager - Orchestrates theme state and effects
 * Refactored from monolithic 524-line file into modular architecture
 * Copyright 2024 Delanoe Pirard / Aedelon - Apache 2.0
 */

import { ThemeState, THEMES } from './ThemeState.js';
import { ThemeEffects } from './ThemeEffects.js';

export class ThemeManager {
  constructor() {
    this.toggle = document.querySelector('.theme-toggle');
    this.state = new ThemeState();
    this.effects = new ThemeEffects(this.state);

    this.init();
  }

  init() {
    this.createEnhancedToggleButton();
    this.applyTheme(false);
    this.bindEvents();
    this.state.setupAutoMode(() => this.applyTheme());
  }

  /**
   * Creates the enhanced toggle button with stars/clouds
   */
  createEnhancedToggleButton() {
    if (!this.toggle) return;

    const generateStarPositions = () => {
      const positions = [];
      const starCount = 12;
      const centerX = 50, centerY = 50, exclusionRadius = 17;

      for (let i = 0; i < starCount; i++) {
        let top, left, attempts = 0;

        do {
          top = Math.random() * 60 + 25;
          left = Math.random() * 60 + 25;
          attempts++;
        } while (
          Math.sqrt(Math.pow(left - centerX, 2) + Math.pow(top - centerY, 2)) < exclusionRadius &&
          attempts < 20
        );

        const distanceFromCenter = Math.sqrt(
          Math.pow(left - centerX, 2) + Math.pow(top - centerY, 2)
        );
        const size = Math.max(1, 2.5 - (distanceFromCenter / 20));

        positions.push({
          top: `${top}%`,
          left: `${left}%`,
          size: `${size}px`,
          delay: `${i * 0.2}s`,
          duration: `${2 + Math.random()}s`
        });
      }
      return positions;
    };

    const stars = generateStarPositions();
    const starsHTML = stars.map(star => `
      <div class="star" style="
        top: ${star.top}; left: ${star.left};
        width: ${star.size}; height: ${star.size};
        animation-delay: ${star.delay};
        animation-duration: ${star.duration};
      "></div>
    `).join('');

    const enhancedHTML = `
      <div class="theme-toggle-container">
        <button class="theme-toggle" aria-label="Toggle theme mode" data-mode="${this.state.currentMode}">
          <div class="clouds">
            <div class="cloud cloud1"></div>
            <div class="cloud cloud2"></div>
          </div>
          <div class="stars">${starsHTML}</div>
          <div class="theme-icon-wrapper">
            <div class="sun-icon">
              <div class="sun-rays">
                ${Array(8).fill('<div class="sun-ray"></div>').join('')}
              </div>
            </div>
            <div class="moon-icon"></div>
            <div class="eclipse-icon"></div>
          </div>
        </button>
      </div>
    `;

    const container = document.createElement('div');
    container.innerHTML = enhancedHTML;
    this.toggle.replaceWith(container.firstElementChild);
    this.toggle = document.querySelector('.theme-toggle');
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Toggle click
    this.toggle?.addEventListener('click', (e) => {
      this.effects.createRippleEffect(e);
      this.cycleTheme();
    });

    // Magnetic effect
    this.toggle?.addEventListener('mousemove', (e) => {
      this.effects.magneticEffect(e, this.toggle);
    });
    this.toggle?.addEventListener('mouseleave', () => {
      this.effects.resetMagneticEffect(this.toggle);
    });

    // Keyboard shortcut (Ctrl/Cmd + Shift + T)
    document.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        this.cycleTheme();
      }
    });
  }

  /**
   * Cycle through theme modes
   */
  cycleTheme() {
    this.state.cycleMode();
    this.applyTheme();

    this.toggle?.setAttribute('data-mode', this.state.currentMode);

    this.effects.createFlashEffect(this.state.getEffectiveTheme() === THEMES.DARK);
    this.showModeNotification();
    this.state.trackChange();
  }

  /**
   * Apply current theme with optional animation
   */
  applyTheme(animate = true) {
    if (this.effects.isTransitioning) return;

    const theme = this.state.getEffectiveTheme();

    if (animate) {
      this.effects.performTransition(theme, () => {
        this.state.applyTheme(theme);
      });
    } else {
      this.state.applyTheme(theme);
    }
  }

  /**
   * Show mode notification
   */
  showModeNotification() {
    const modeNames = {
      [THEMES.LIGHT]: '☀️ Light Mode',
      [THEMES.DARK]: '🌙 Dark Mode',
      [THEMES.AUTO]: '🌗 Auto Mode'
    };
    this.effects.showNotification(modeNames[this.state.currentMode]);
  }

  // ============ Public API ============

  getTheme() {
    return this.state.getThemeInfo();
  }

  setMode(mode) {
    if (this.state.setMode(mode)) {
      this.applyTheme();
    }
  }

  setThemeDirectly(theme) {
    if (theme === THEMES.LIGHT || theme === THEMES.DARK) {
      this.state.applyTheme(theme);
    }
  }

  getAvailableModes() {
    return Object.values(THEMES);
  }

  isAutoMode() {
    return this.state.currentMode === THEMES.AUTO;
  }

  getSystemPreference() {
    return this.state.getSystemPreference();
  }

  destroy() {
    this.state.destroy();
  }
}
